import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtFecha } from "../../lib/format";
import { paisNombre } from "../../lib/kycData";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { VersionLista } from "./ScreeningResults";

const OF = new Set(["compliance_officer", "owner_oficial"]);
const OW = new Set(["owner", "owner_oficial"]);

const EST = { coincide: "text-rose-700 font-semibold", difiere: "text-emerald-700", no_disponible: "text-slate-400" };

const Fila = ({ k, a, b, est }) => (
  <tr className="border-t align-top">
    <td className="py-1 pr-2 text-slate-500">{k}</td><td className="pr-2">{a || "—"}</td><td className="pr-2">{b || "—"}</td>
    <td className={EST[est] || ""}>{est || ""}</td>
  </tr>
);

const Extra = ({ m, fuente }) => {
  const x = m.extra || {};
  if (fuente === "pep") {
    return (
      <div className="text-xs mt-2 space-y-0.5" data-testid="case-pep-extra">
        <p><b>Cargo:</b> {x.cargo_pep || x.cargo} · <b>Institución:</b> {x.institucion_pep || x.institucion} ({x.categoria_h3})</p>
        <p><b>Última declaración:</b> {x.ultima_declaracion} ({x.tipo_ultima_declaracion}) · <b>PEP hasta:</b> {x.pep_hasta || "cargo vigente"}{x.motivo_pep_hasta ? ` (${x.motivo_pep_hasta})` : ""}</p>
        <p className="text-slate-500">{x.atribucion}. Coincidencia solo por nombre: el catálogo no trae RUT ni fecha de nacimiento.</p>
      </div>
    );
  }
  return <p className="text-xs mt-2 text-slate-600">{fuente === "onu" ? `Ref. ${x.referencia} · régimen ${x.lista} · incluido el ${x.incluido_el}` : fuente === "ofac" ? `UID ${x.uid} · programas ${(x.programas || []).join(", ")}` : x.nota}</p>;
};

export const Coincidencia = ({ m, c, fuente }) => (
  <div className="border border-slate-200 rounded-lg p-3" data-testid={`case-match-${m.ext_id}`}>
    <p className="text-sm"><b>{m.nombre}</b> · puntaje <span className="font-mono">{m.puntaje}</span>{m.alias?.length ? ` · alias: ${m.alias.join("; ")}` : ""}</p>
    <table className="w-full text-xs mt-2">
      <thead><tr className="text-left text-slate-500"><th>Campo</th><th>Cliente</th><th>Registro de la lista</th><th>Cruce</th></tr></thead>
      <tbody>
        <Fila k="Nombre" a={`${c.nombres} ${c.apellido_paterno} ${c.apellido_materno || ""}`} b={m.nombre_coincidente} />
        <Fila k="Fecha de nacimiento" a={c.fecha_nacimiento} b={m.fechas_nac.join(", ")} est={m.cruce.fecha_nacimiento} />
        <Fila k="Nacionalidad" a={paisNombre(c.nacionalidad)} b={m.nacionalidades.join(", ")} est={m.cruce.nacionalidad} />
        <Fila k="Documento" a={c.documento} b={m.documentos.join(", ")} est={m.cruce.documento} />
      </tbody>
    </table>
    <Extra m={m} fuente={fuente} />
  </div>
);

const Form = ({ fields, buttons, onSend }) => {
  const [v, setV] = useState({});
  const ok = fields.every((f) => (v[f.k] || "").trim().length >= 10);
  return (
    <div className="space-y-2 border-t pt-3">
      {fields.map((f) => <Textarea key={f.k} placeholder={`${f.l} (mínimo 10 caracteres)`} value={v[f.k] || ""} onChange={(e) => setV({ ...v, [f.k]: e.target.value })} data-testid={`case-field-${f.k}`} />)}
      <div className="flex flex-wrap gap-2">
        {buttons.map(([val, label]) => (
          <Button key={label} size="sm" disabled={!ok} className="bg-[#1B2A41] hover:bg-[#24375a]" onClick={() => onSend(val, v)} data-testid={`case-btn-${label.toLowerCase().replace(/\W+/g, "-")}`}>{label}</Button>
        ))}
      </div>
    </div>
  );
};

export const CaseActions = ({ c, rol, onDone }) => {
  const send = async (path, body) => {
    try {
      await api.post(`/company/cases/${c.id}/${path}`, body);
      toast.success("Registrado");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const listas = ["posible_coincidencia", "pep_detectado"].includes(c.tipo);
  if (c.estado === "abierto" && listas && OF.has(rol)) {
    return <Form fields={[{ k: "motivo", l: "Motivo de la decisión" }]}
      buttons={[["homonimo_descartado", "Descartar: es homónimo"], ["coincidencia_confirmada", c.tipo === "pep_detectado" ? "Confirmar: es PEP" : "Confirmar coincidencia"]]}
      onSend={(d, v) => send("decision", { decision: d, motivo: v.motivo })} />;
  }
  if (c.estado === "pendiente_ddr" && OF.has(rol)) {
    return <Form fields={[{ k: "origen_fondos", l: "Origen de los fondos" }, { k: "origen_patrimonio", l: "Origen del patrimonio" }, { k: "motivo", l: "Fundamento" }]}
      buttons={[[true, "Aprobar y enviar a alta gerencia"], [false, "No aprobar"]]}
      onSend={(a, v) => send("pep-ddr", { ...v, aprueba: a })} />;
  }
  if (c.estado === "pendiente_owner" && OW.has(rol)) {
    return <Form fields={[{ k: "motivo", l: "Fundamento de la alta gerencia" }]}
      buttons={[[true, "Aprobar relación con PEP"], [false, "Rechazar relación con PEP"]]}
      onSend={(a, v) => send("aprobacion-owner", { aprueba: a, motivo: v.motivo })} />;
  }
  if ((c.estado === "pendiente_cierre" || (c.estado === "abierto" && !listas)) && OF.has(rol)) {
    return (
      <div data-testid="case-close-form">
        <p className="text-xs text-slate-600 mt-2">Cierre del caso (Circular 62, c.8){c.ros_inmediato ? ": la coincidencia ONU exige ROS" : ""}.</p>
        <Form fields={[{ k: "motivo", l: "Motivo" }, { k: "descripcion", l: "Descripción" }, { k: "conclusion", l: "Conclusión" }]}
          buttons={c.ros_inmediato ? [["ros", "Cerrar con ROS"]] : [["ros", "Cerrar con ROS"], ["descarte", "Cerrar como descarte"]]}
          onSend={(r, v) => send("cerrar", { ...v, resultado: r })} />
      </div>
    );
  }
  return null;
};

export const CaseHistory = ({ c }) => (
  <div className="text-xs space-y-1" data-testid="case-history">
    {c.decision && <p><b>Decisión del oficial:</b> {c.decision.decision} · {c.decision.nombre} · {fmtFecha(c.decision.fecha)} · {c.decision.motivo}</p>}
    {c.cierre && <p data-testid="case-cierre"><b>Cierre:</b> {c.cierre.resultado === "ros" ? "ROS" : "descarte"} · {c.cierre.nombre} · {fmtFecha(c.cierre.fecha)} · motivo: {c.cierre.motivo} · descripción: {c.cierre.descripcion} · conclusión: {c.cierre.conclusion}</p>}
    {c.ddr && <p><b>Debida diligencia reforzada:</b> fondos: {c.ddr.origen_fondos} · patrimonio: {c.ddr.origen_patrimonio} · {c.ddr.aprueba ? "aprobada" : "no aprobada"} por {c.ddr.nombre} ({fmtFecha(c.ddr.fecha)})</p>}
    {c.aprobacion_owner && <p><b>Alta gerencia:</b> {c.aprobacion_owner.aprueba ? "aprueba" : "rechaza"} · {c.aprobacion_owner.nombre} · {fmtFecha(c.aprobacion_owner.fecha)} · {c.aprobacion_owner.motivo}</p>}
    <p className="text-slate-500">Lista consultada: <VersionLista v={c.version_lista} /></p>
  </div>
);
